import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';
import { type CanvasHTMLElement } from '@canvas/input-types/base-event-types';

const allowNativeContextMenuSelectors = 'input, textarea, [contenteditable="true"]';

export const setupIgnoredEvents = (root: CanvasHTMLElement): (() => void) => {
  const preventDefault = (evt: Event) => {
    const target = evt.target;
    const closest =
      target != null && 'closest' in target && typeof target.closest === 'function'
        ? target.closest.bind(target)
        : null;

    if (closest?.('[data-allow-native-drag="true"]')) {
      return;
    }

    evt.preventDefault();
  };

  /**
   * Suppress the native browser context menu across the canvas root, except inside
   * text-editing surfaces (contenteditable, input, textarea) where the native menu
   * provides spell check, dictionary, and paste.
   *
   * The W3C Pointer Events spec leaves contextmenu timing OS-dependent:
   * - Windows/Linux: mousedown -> mouseup -> contextmenu
   * - macOS: mousedown -> contextmenu -> mouseup
   *
   * On Windows this means contextmenu fires at the same moment the canvas state
   * machine processes secondary pointerUp and sends ShowContextMenuCommand, so both
   * menus appear at once. On macOS the OS captures the event loop after showing the
   * native menu, so pointerUp never fires and the command is never sent.
   *
   * Suppression cannot be unconditional: on macOS, preventing the native menu also
   * prevents the OS from capturing the event loop, so pointerUp does fire — and in
   * the focusing state the machine does not send ShowContextMenuCommand, leaving no
   * menu at all.
   *
   * The selector allowlist mirrors the pattern in
   * services/databases/src/app/bridge/handlers/disable-native-context-menu.ts and
   * aligns with the context-menu-machine's `not(isFocused)` guard — when an entity is
   * focused its editor DOM has contenteditable="true", so the native menu is allowed
   * here and the machine does not fire ShowContextMenuCommand there. Both sides
   * agree, so no double menu is possible.
   *
   * Ref: https://developer.mozilla.org/en-US/docs/Mozilla/Add-ons/WebExtensions/API/browserSettings/contextMenuShowEvent
   * Ref: https://w3c.github.io/pointerevents/
   */
  const preventNativeContextMenu = (evt: Event) => {
    if (!isFeatureFlagEnabled('rightClickIgnore')) {
      return;
    }

    const target = evt.target instanceof HTMLElement ? evt.target : null;

    /**
     * `closest` rather than `matches` because the click target is usually a child
     * element inside the editing surface, not the surface itself.
     */
    if (target?.closest(allowNativeContextMenuSelectors)) {
      return;
    }

    evt.preventDefault();
  };

  root.addEventListener('dragstart', preventDefault);
  root.addEventListener('contextmenu', preventNativeContextMenu, true);

  return () => {
    root.removeEventListener('dragstart', preventDefault);
    root.removeEventListener('contextmenu', preventNativeContextMenu, true);
  };
};
